/**
 * Newsletter Service for trAInedup.ai
 * Handles newsletter subscriptions and email collection
 */

import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { db, storage } from './config';

/**
 * Available ebook options
 */
export interface Ebook {
  id: string;
  title: string;
  description: string;
  filename: string;
  storagePath: string;
}

/**
 * Available ebooks for newsletter signup
 */
export const AVAILABLE_EBOOKS: Ebook[] = [
  {
    id: 'agents',
    title: 'What is an AI Agent?',
    description: 'Learn what AI Agents offer beyond AI Chatbots',
    filename: 'AI Chatbot vs AI Agent E-book.pdf',
    storagePath: 'ebooks/AI Chatbot vs AI Agent E-book.pdf'
  },
  {
    id: 'adcreative',
    title: 'Mastering AdCreative.ai',
    description: 'Learn to create high-converting ad creatives using AI',
    filename: 'Mastering AdCreative.ai E-book.pdf',
    storagePath: 'ebooks/Mastering AdCreative.ai E-book.pdf'
  },
  {
    id: 'prompts',
    title: 'Mastering AI Prompts',
    description: 'Advanced techniques for crafting effective AI prompts',
    filename: 'Mastering AI Prompts E-book.pdf',
    storagePath: 'ebooks/Mastering AI Prompts E-book.pdf'
  },
  {
    id: 'gretel',
    title: 'Mastering Gretel.ai',
    description: 'Generate synthetic data for privacy-safe AI training',
    filename: 'Mastering Gretel.ai E-book.pdf',
    storagePath: 'ebooks/Mastering Gretel.ai E-book.pdf'
  },
  {
    id: 'jasper',
    title: 'Mastering Jasper AI',
    description: 'Professional content creation with AI assistance',
    filename: 'Mastering Jasper AI E-book.pdf',
    storagePath: 'ebooks/Mastering Jasper AI E-book.pdf'
  },
  {
    id: 'turnitin',
    title: 'Mastering Turnitin',
    description: 'Academic integrity and plagiarism detection mastery',
    filename: 'Mastering Turnitin E-book.pdf',
    storagePath: 'ebooks/Mastering Turnitin E-book.pdf'
  }
];

/**
 * Newsletter subscriber interface
 */
export interface NewsletterSubscriber {
  id?: string;
  email: string;
  subscribedAt: Timestamp;
  source: string;
  isActive: boolean;
  selectedEbook?: string; // ID of the selected ebook
  ebookDownloaded?: boolean; // Track if ebook was downloaded
  tags?: string[];
}

/**
 * Newsletter service class
 */
class NewsletterService {
  private readonly collectionName = 'newsletters';
  private lastSubmissionTime: number = 0;
  private readonly RATE_LIMIT_MS = 30000; // 30 seconds between submissions

  /**
   * Add a new newsletter subscriber
   */
  async addSubscriber(
    email: string, 
    source: string = 'pre-launch-page',
    selectedEbook?: string,
    tags: string[] = []
  ): Promise<{ subscriberId: string; downloadUrl?: string }> {
    try {
      // Rate limiting check
      const now = Date.now();
      if (now - this.lastSubmissionTime < this.RATE_LIMIT_MS) {
        const waitTime = Math.ceil((this.RATE_LIMIT_MS - (now - this.lastSubmissionTime)) / 1000);
        throw new Error(`Please wait ${waitTime} seconds before subscribing again.`);
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error('Please enter a valid email address.');
      }

      // Length validation
      if (email.length > 254) {
        throw new Error('Email address is too long.');
      }
      // Temporarily skip duplicate check due to Firestore rules issues
      // TODO: Re-enable when App Check is properly configured
      /*
      const existingSubscriber = await this.getSubscriberByEmail(email);
      
      if (existingSubscriber) {
        throw new Error('This email is already subscribed to our newsletter.');
      }
      */

      // Validate ebook selection if provided
      if (selectedEbook && !AVAILABLE_EBOOKS.find(ebook => ebook.id === selectedEbook)) {
        throw new Error('Invalid ebook selection.');
      }

      // Add new subscriber
      const subscriberData: Omit<NewsletterSubscriber, 'id'> = {
        email: email.toLowerCase().trim(),
        subscribedAt: serverTimestamp() as Timestamp,
        source,
        isActive: true,
        selectedEbook,
        ebookDownloaded: false,
        tags,
      };

      const docRef = await addDoc(collection(db, this.collectionName), subscriberData);
      
      // Update rate limiting timestamp
      this.lastSubmissionTime = Date.now();
      
      // Generate download URL if ebook was selected
      let downloadUrl: string | undefined;
      if (selectedEbook) {
        try {
          downloadUrl = await this.generateEbookDownloadUrl(selectedEbook);
        } catch (error: any) {
          // Don't fail the signup if ebook generation fails
          // Newsletter signup should still work even if ebooks aren't uploaded yet
        }
      }
      
      return { 
        subscriberId: docRef.id,
        downloadUrl 
      };
    } catch (error: any) {
      throw new Error(error.message || 'Failed to subscribe to newsletter');
    }
  }

  /**
   * Get subscriber by email
   */
  async getSubscriberByEmail(email: string): Promise<NewsletterSubscriber | null> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email.toLowerCase().trim())
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as NewsletterSubscriber;
    } catch (error: any) {
      throw new Error('Failed to check subscription status');
    }
  }

  /**
   * Get all subscribers (admin function)
   */
  async getAllSubscribers(): Promise<NewsletterSubscriber[]> {
    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as NewsletterSubscriber[];
    } catch (error: any) {
      throw new Error('Failed to fetch subscribers');
    }
  }

  /**
   * Get subscriber count
   */
  async getSubscriberCount(): Promise<number> {
    try {
      const querySnapshot = await getDocs(
        query(
          collection(db, this.collectionName),
          where('isActive', '==', true)
        )
      );
      
      return querySnapshot.size;
    } catch (error: any) {
      return 0;
    }
  }

  /**
   * Unsubscribe user (for future use)
   */
  async unsubscribe(email: string): Promise<void> {
    try {
      const subscriber = await this.getSubscriberByEmail(email);
      
      if (!subscriber || !subscriber.id) {
        throw new Error('Subscriber not found');
      }

      // Note: Would need updateDoc import and implementation
      // For now, just throw an error for unsubscribe requests
      throw new Error('Unsubscribe functionality not yet implemented');
    } catch (error: any) {
      throw new Error('Failed to unsubscribe');
    }
  }

  /**
   * Generate a secure download URL for an ebook
   */
  async generateEbookDownloadUrl(ebookId: string): Promise<string> {
    const ebook = AVAILABLE_EBOOKS.find(e => e.id === ebookId);
    if (!ebook) {
      throw new Error('Ebook not found');
    }

    try {
      const storageRef = ref(storage, ebook.storagePath);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      throw new Error('Failed to generate download link');
    }
  }

  /**
   * Get ebook information by ID
   */
  getEbookById(ebookId: string): Ebook | undefined {
    return AVAILABLE_EBOOKS.find(ebook => ebook.id === ebookId);
  }
}

// Export singleton instance
export const newsletterService = new NewsletterService();

